import { BreakpointObserver } from "@angular/cdk/layout";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewContainerRef,
} from "@angular/core";
import { FormBuilder, FormControl, Validators } from "@angular/forms";
import { MediaQuery } from "@bca-core/models/_enums";
import { DialogService } from "@bca-core/modules/dialog/services/dialog.service";
import { PageScrollService } from "@bca-core/modules/page-scroll/services/page-scroll.service";
import { AvoTrackingService } from "@bca-core/services/avo-tracking.service";
import { UIService } from "@bca-core/services/ui.service";
import * as receiptActions from "@bca-receipt/store/receipt.actions";
import {
  DialogIdentifier,
  Gender,
  ScrollPositionId,
} from "@bca-shared/models/_enums";
import { DialogId } from "@bca-shared/models/_types";
import { IAddress, ISearchableAddress } from "@bca-shared/models/address";
import { Form } from "@bca-shared/models/form";
import { IProduct } from "@bca-shared/models/product";
import { IUser } from "@bca-shared/models/user";
import { AvoDataTransformService } from "@bca-shared/utils/avo/avo-data-transform.service";
import { AvoService } from "@bca-shared/utils/avo/avo.service";
import * as appActions from "@bca-store/app.actions";
import { IAction } from "@bca-store/models/action";
import { createAction } from "@bca-store/models/action-creator";
import { IRootState } from "@bca-store/store.reducers";
import {
  selectAllProducts,
  selectIncompletedProducts,
  selectTotalProductCount,
} from "@bca-store/store.selectors";
import { CountryCode, ProductStart } from "@bca-user-data/models/_enums";
import { ICountry, ICountrySettings } from "@bca-user-data/models/country";
import { IDate } from "@bca-user-data/models/date";
import { IFaceImage } from "@bca-user-data/models/face-image";
import * as userDataActions from "@bca-user-data/store/user-data.actions";
import {
  createAddressForm,
  createUserDataForm,
  getCountrySettings,
} from "@bca-user-data/user-data.constants";
import { ImageUploadDialog } from "@bca-user-data/user-data/choose-image/image-upload/image-upload-dialog";
import { environment } from "@environments/environment";
import { select, Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import moment from "moment/moment";
import { BehaviorSubject, Observable, of, Subscription } from "rxjs";
import {
  combineLatestWith,
  filter,
  map,
  take,
  takeWhile,
  tap,
  withLatestFrom,
} from "rxjs/operators";
import { AvoEventTypes } from "../../../shared/utils/avo/avo-event-details.models";
import { datepickerClick } from '../../../shared/utils/avo/Avo';

@Component({
  selector: "bca-user-data",
  templateUrl: "user-data.component.html",
  styleUrls: ["user-data.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserDataComponent
  extends Form<IUser>
  implements OnInit, OnDestroy
{
  private lastReportedEmailError: string | undefined;
  private lastReportedLastNameError: string | undefined;
  private lastReportedInitialsError: string | undefined;
  private lastReportedGenderError: string | undefined;

  mobileQueryMatches$ = this._breakPoint
    .observe(MediaQuery.MAX_WIDTH_912)
    .pipe(map(({ matches }) => matches));
  selectedProduct$: Observable<IProduct>;
  allowNewProduct$: BehaviorSubject<boolean>;
  totalProductCount$: Observable<number>;
  countries$: Observable<ICountry[]>;
  middleNames$: Observable<string[]>;
  products: IProduct[];
  selectedCountryCode$: Observable<CountryCode>;
  selectedCountrySettings$: Observable<ICountrySettings>;
  selectedUser$: Observable<IUser>;
  selectedBirthDate$: Observable<IDate>;
  selectedAddress$: Observable<IAddress>;
  selectedUseProduct$: Observable<{
    type: ProductStart;
    date?: Date;
  }>;
  selectedFaceImage$: Observable<IFaceImage>;
  incompletedProducts$: Observable<IProduct[]>;
  addressLoading$: Observable<boolean>;
  addressManualMode$: Observable<boolean>;
  addressSearchFailed$: Observable<boolean>;
  genders: {
    value: Gender;
    displayText: string;
  }[] = [
    { value: Gender.MALE, displayText: "INPUT.MALE" },
    { value: Gender.FEMALE, displayText: "INPUT.FEMALE" },
    { value: Gender.NOT_RELEVANT, displayText: "INPUT.NOT_RELEVANT" },
  ];

  startUsageDirectly: ProductStart = ProductStart.DIRECTLY;
  initialSelectedCountry: ICountry = {
    label: "Nederland",
    value: CountryCode.NL,
  };
  addExtraCardDestination = "https://www.ns.nl/businesscardaanvragen";

  hasInteracted = false;

  subscriptions = new Subscription();
  constructor(
    private _store$: Store<IRootState>,
    public _uiService: UIService,
    private _translate: TranslateService,
    private _formBuilder: FormBuilder,
    private _dialogService: DialogService,
    private _breakPoint: BreakpointObserver,
    private _page: PageScrollService,
    private _viewContainerRef: ViewContainerRef,
    private cdr: ChangeDetectorRef,
    private _avoTransformService: AvoDataTransformService,
    private _avoService: AvoService,
    private _avoTrackingService: AvoTrackingService,
  ) {
    super(createUserDataForm(_formBuilder));
    this.subscriptions = new Subscription();
    this.initializeObservables();
  }

  ngOnInit(): void {
    this.setupInitialData();
    this.setupFormSubscriptions();
    this.dispatchInitialActions();

    const beginCheckoutData =
      this._avoTransformService.transformBeginCheckoutAction(this.products);
    this._avoService.beginCheckout(beginCheckoutData);
  }

  private initializeObservables(): void {
    this.mobileQueryMatches$ = this._breakPoint
      .observe(MediaQuery.MAX_WIDTH_912)
      .pipe(map(({ matches }) => matches));

    this.allowNewProduct$ = this._uiService.allowNewProduct$;
    this.middleNames$ = this._store$.pipe(
      select(({ userData }) => userData.middleNames),
    );

    this.incompletedProducts$ = this._store$.pipe(
      select(selectIncompletedProducts),
    );
    this.addressSearchFailed$ = this._store$.pipe(
      select(({ userData }) => userData.addressSearchFailed),
    );
    this.addressLoading$ = this._store$.pipe(
      select(({ userData }) => userData.addressLoading),
    );
    this.selectedProduct$ = this._store$.pipe(
      select(({ userData }) => userData.selectedProduct),
    );
    this.selectedUser$ = this.selectedProduct$.pipe(
      filter((selectedProduct) => selectedProduct != null),
      map(({ user }) => user),
    );
    this.selectedAddress$ = this.selectedUser$.pipe(
      map((user) => (user ? user.address : null)),
    );
    this.selectedBirthDate$ = this.selectedUser$.pipe(
      map((selectedUser) => {
        if (selectedUser && selectedUser.birthDate) {
          const { dateString: d } = selectedUser.birthDate;
          return { dateString: d ? d : "" } as IDate;
        }
        return { dateString: "" } as IDate;
      }),
    );
    this.addressManualMode$ = this._store$.pipe(
      select(({ userData }) => userData.addressManualModeState),
      combineLatestWith(this.selectedProduct$),
      filter(([, selectedProduct]) => selectedProduct != null),
      map(([addressManualModeState, { id }]) => !!addressManualModeState[id]),
    );

    this.selectedUseProduct$ = this.selectedUser$.pipe(
      map((selectedUser) => selectedUser.useProduct),
    );
    this.selectedFaceImage$ = this.selectedUser$.pipe(
      filter((selectedUser) => selectedUser != null),
      map(({ image }) => image),
    );

    this.totalProductCount$ = this._store$.pipe(
      select(selectTotalProductCount),
    );
  }

  private setupInitialData(): void {
    // used for AVO event
    const { subscriptions } = this;
    subscriptions.add(
      this._store$
        .pipe(select(selectAllProducts))
        .subscribe((products: IProduct[]) => {
          this.products = products;
        }),
    );

    this.setAsyncValidators(
      this.emailDuplicateValidator.bind(this),
      this.getControl("email"),
    );

    this.countries$ = this._translate.get("USER_DATA.COUNTRY").pipe(
      map((translation: { [key in CountryCode]: string }) => {
        const countryCodes = Object.keys(translation) as CountryCode[];
        return countryCodes.map((value) => ({
          value,
          label: translation[value],
        }));
      }),
    );

    this.selectedCountryCode$ = this.selectedProduct$.pipe(
      map((selectedProduct) => {
        if (selectedProduct && selectedProduct.user) {
          const { country } = selectedProduct.user;
          return country ? country.value : CountryCode.NL;
        }
        return CountryCode.NL;
      }),
    );

    this.selectedCountrySettings$ = this.selectedCountryCode$.pipe(
      map((countryCode) => getCountrySettings(countryCode)),
      tap((settings) => {
        const length = settings.postcodeLength;
        this.setValidators(
          [
            Validators.required,
            Validators.maxLength(length),
            Validators.minLength(length - 1),
          ],
          this.getControl("address.postcode"),
        );
      }),
    );
  }

  onFirstInteraction(event) {
    if (!this.hasInteracted) {
      this.hasInteracted = true;
      const id = event.target?.id;
      this.handleAvoEvents("form_start", null, id);
    }
  }

  private setupFormSubscriptions(): void {
    const { subscriptions } = this;

    subscriptions.add(
      this.selectedProduct$.subscribe((selectedProduct) => {
        if (selectedProduct && selectedProduct.user) {
          this.formGroup?.reset();
          this.patchValue(selectedProduct.user);
          this.cdr.markForCheck();
        } else {
          this.resetValue();
          this._uiService.getUserStorage();
        }

        if (selectedProduct && selectedProduct.needsPhoto) {
          this.setValidators(Validators.required, this.getControl("image"));
        } else {
          this.clearValidators(this.getControl("image"));
        }
      }),
    );

    subscriptions.add(
      this._uiService.nextPageClicked$.subscribe(() => this.formSubmit()),
    );

    subscriptions.add(
      this._uiService.addProductClicked$.subscribe(() => this.addNewProduct()),
    );

    subscriptions.add(
      this._uiService.tabClicked$
        .pipe(filter((tab) => tab.done))
        .subscribe(() => this.validate()),
    );

    subscriptions.add(
      this._uiService.editProductClicked$.subscribe(() => this.validate()),
    );

    subscriptions.add(
      this.selectedUser$.pipe(take(1)).subscribe((selectedUser) => {
        if (!selectedUser) {
          this.onProductUsageSelected(this.startUsageDirectly);
          this.onProductUsageDateSelected(
            moment()
              .add(environment.userData.useProductMinDays, "days")
              .toDate(),
          );
          this.onCountrySelected(this.initialSelectedCountry.value);
        }
      }),
    );
  }

  private dispatchInitialActions(): void {
    this._store$.dispatch(new userDataActions.FetchMiddleNames());

    this.selectedProduct$
      .pipe(
        take(1),
        withLatestFrom(
          this.incompletedProducts$,
          this._store$.pipe(select(selectAllProducts)),
        ),
      )
      .subscribe(([selectedProduct, incompletedProducts, products]) => {
        const nextProduct =
          selectedProduct || incompletedProducts.shift() || products.shift();
        this._store$.dispatch(new userDataActions.SelectProduct(nextProduct));
        this._uiService.getUserStorage();
      });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  addNewProduct(): void {
    this.handleAvoEvents(
      "add_extra_card_link_click",
      this.addExtraCardDestination,
    );
    this.validate();

    this._store$.dispatch(
      this._createAction(new userDataActions.SelectProduct(null)),
    );
    this._uiService.getUserStorage();
    this._store$.dispatch(this._createAction(new appActions.PreviousRoute()));
  }

  onImageDialogOpened(): void {
    this.dispatchUserToStore();
    const imageDialog = this._dialogService.open(
      ImageUploadDialog,
      DialogIdentifier.Image,
      this._viewContainerRef,
      null,
      null,
      (visible: boolean, identifier: DialogId) => {
        this._store$.dispatch(
          this._createAction(
            new appActions.SetDialogStatus({ visible, identifier }),
          ),
        );
      },
    );

    imageDialog
      .beforeClosed()
      .pipe(
        takeWhile(() => imageDialog.instance != null),
        filter(() => this._breakPoint.isMatched(MediaQuery.MAX_WIDTH_640)),
      )
      .subscribe(() =>
        this._page.scrollTo(ScrollPositionId.IMAGE, { timeout: 375 }),
      );
  }

  onImageDelete(): void {
    this.dispatchUserToStore({ image: null, valid: false });
  }

  onProductUsageDateSelected(date: Date): void {
    this.dispatchUserToStore({
      useProduct: {
        ...this.formGroup.value.useProduct,
        date,
      },
    });

    this.handleAvoEvents("datepickerClick", null, null, null, date);
  }

  onProductUsageSelected(type: ProductStart): void {
    this.handleAvoEvents("selectable_click", type);
    this.dispatchUserToStore({
      useProduct: {
        ...this.formGroup.value.useProduct,
        type,
      },
    });
  }

  onAddressSearch(address: ISearchableAddress): void {
    this.dispatchUserToStore();
    this._store$.dispatch(
      this._createAction(new userDataActions.FetchAddress(address)),
    );
  }

  onAddressManual(product: IProduct, manual: boolean): void {
    this._store$.dispatch(
      this._createAction(
        new userDataActions.SetAddressManualMode({
          productId: String(product.id),
          manual,
        }),
      ),
    );
    this._store$.dispatch(
      this._createAction(
        new userDataActions.SetAddressSearchFailed({ failed: false }),
      ),
    );
  }

  async onCountrySelected(
    countryCode: CountryCode,
    product?: IProduct,
  ): Promise<void> {
    const { countries$ } = this;
    const { value, label } = (await countries$.toPromise()).find(
      (country) => country.value === countryCode,
    ) || { value: countryCode, label: "" };

    this.dispatchUserToStore({
      country: { value, label },
      address: { ...createAddressForm(this._formBuilder).value },
    });

    if (product) {
      this._store$.dispatch(
        this._createAction(
          new userDataActions.SetAddressManualMode({
            productId: String(product.id),
            manual: false,
          }),
        ),
      );
    }
    this._store$.dispatch(
      this._createAction(
        new userDataActions.SetAddressSearchFailed({ failed: false }),
      ),
    );
  }

  selectedABirthdate(date: string): void {
    const formattedDate = date && date.length >= 7 ? date : "";

    if (!formattedDate) {
      this.onBirthDateSelected(null);
    } else {
      this.dispatchUserToStore({
        birthDate: {
          ...this.formGroup.value.birthDate,
          dateString: formattedDate,
        },
      });
    }
  }

  onBirthDateSelected(date: Date): void {
    const inputDate = date ? new Date(date) : null;
    const { formGroup } = this;
    const user = formGroup.value as IUser;
    if (inputDate) {
      this.patchValue({
        ...user,
        birthDate: {
          ...user.birthDate,
          date: inputDate,
        },
      });
    } else {
      this.patchValue({
        ...user,
        birthDate: {
          dateString: "",
          date: null,
        },
      });
    }
  }

  emailDuplicateValidator(
    control: FormControl,
  ): Observable<{ [key: string]: boolean } | null> {
    const emailToCheck = control.value?.trim();

    if (!emailToCheck) {
      return of(null);
    }

    return this._store$.pipe(
      take(1),
      select(({ receipt }) => receipt.products),
      map((products) =>
        products
          .map((product) => product.user)
          .filter((user) => user && user.id),
      ),
      withLatestFrom(this.selectedProduct$),
      filter(([, selectedProduct]) => selectedProduct?.user != null),
      map(([users, { user: selectedUser }]) => {
        const isDuplicate = users.some(
          ({ email, id }) => email === emailToCheck && id !== selectedUser.id,
        );

        return isDuplicate ? { duplicate: true } : null;
      }),
    );
  }

  previousRoute(): void {
    this.validate();
    this.handleAvoEvents("link_click");
    this._store$.dispatch(this._createAction(new appActions.PreviousRoute()));
  }

  formSubmit(): void {
    const isFormValid = this.validate();
    if (!isFormValid) {
      this.scrollToPosition(ScrollPositionId.INPUT_INVALID, 275, 275);
      return;
    }
    this.handleAvoEvents("button_click");
    this.handleAvoEvents("form_submit");

    this.handleIncompleteProducts();
  }

  validate(): boolean {
    const { formGroup } = this;
    const isFormValid = formGroup.valid;

    if (isFormValid) {
      this.dispatchUserToStore();
      this._refreshFormForNewUser();
    } else {
      this._uiService.getUserStorage();
      this.touchControls();
    }

    return isFormValid;
  }

  getGenderErrorKey(): string | undefined {
    const formTagId = "gender";
    let errorMessage;
    if (this.isTouched("gender") && this.hasError("gender", "required")) {
      errorMessage = this._translate.instant("INPUT.GENDER.ERROR_REQUIRED");
    } else {
      errorMessage = undefined;
    }

    if (errorMessage && errorMessage !== this.lastReportedGenderError) {
      this.handleAvoEvents("form_error", null, formTagId, errorMessage);
      this.lastReportedGenderError = errorMessage; // Update the last reported error
    }

    if (!errorMessage) {
      this.lastReportedGenderError = undefined;
    }

    return errorMessage;
  }

  getInitialsErrorKey(): string | undefined {
    const formTagId = "initials";
    let errorMessage;
    if (
      this.isTouchedAndDirty("email") &&
      this.hasError("initials", "required")
    ) {
      errorMessage = this._translate.instant("INPUT.INITIALS.ERROR_REQUIRED");
    } else {
      errorMessage = undefined;
    }

    if (errorMessage && errorMessage !== this.lastReportedInitialsError) {
      this.handleAvoEvents("form_error", null, formTagId, errorMessage);
      this.lastReportedInitialsError = errorMessage; // Update the last reported error
    }

    if (!errorMessage) {
      this.lastReportedInitialsError = undefined;
    }

    return errorMessage;
  }

  getLastNameErrorKey(): string | undefined {
    const formTagId = "lastName";
    let errorMessage;
    if (
      this.isTouchedAndDirty("lastName") &&
      this.hasError("lastName", "required")
    ) {
      errorMessage = this._translate.instant("INPUT.LASTNAME.ERROR_REQUIRED");
    } else {
      errorMessage = undefined;
    }

    if (errorMessage && errorMessage !== this.lastReportedLastNameError) {
      this.handleAvoEvents("form_error", null, formTagId, errorMessage);
      this.lastReportedLastNameError = errorMessage; // Update the last reported error
    }

    if (!errorMessage) {
      this.lastReportedLastNameError = undefined;
    }

    return errorMessage;
  }

  getEmailErrorKey(): string | undefined {
    const formTagId = "email";
    let errorMessage: string | undefined;

    if (this.isTouchedAndDirty("email") && this.hasError("email", "required")) {
      errorMessage = this._translate.instant("INPUT.EMAIL.ERROR_REQUIRED");
    } else if (
      this.isTouchedAndDirty("email") &&
      this.hasError("email", "pattern") &&
      !this.hasError("email", "required")
    ) {
      errorMessage = this._translate.instant("INPUT.EMAIL.ERROR_INVALID");
    } else if (
      this.isTouchedAndDirty("email") &&
      this.hasError("email", "duplicate") &&
      !this.hasError("email", "required")
    ) {
      errorMessage = this._translate.instant("INPUT.EMAIL.ERROR_DUPLICATE");
    } else {
      errorMessage = undefined;
    }

    if (errorMessage && errorMessage !== this.lastReportedEmailError) {
      this.handleAvoEvents("form_error", null, formTagId, errorMessage);
      this.lastReportedEmailError = errorMessage;
    }

    if (!errorMessage) {
      this.lastReportedEmailError = undefined;
    }

    return errorMessage;
  }

  private handleIncompleteProducts(): void {
    this.incompletedProducts$.pipe(take(1)).subscribe((products) => {
      if (products.length > 0) {
        const nextProduct = products[0];
        this.selectProduct(nextProduct);
        this.onProductUsageSelected(this.startUsageDirectly);
        this.onProductUsageDateSelected(
          moment().add(environment.userData.useProductMinDays, "days").toDate(),
        );
        this.onCountrySelected(this.initialSelectedCountry.value);
      } else {
        this._store$.dispatch(this._createAction(new appActions.NextRoute()));
      }
    });
  }

  private selectProduct(product: any): void {
    this._uiService.getUserStorage();
    this._store$.dispatch(
      this._createAction(new userDataActions.SelectProduct(product)),
    );

    const birthDate = product?.user?.birthDate?.date;
    if (birthDate) {
      this.onBirthDateSelected(birthDate);
    } else {
      this.selectedABirthdate("");
    }

    this.scrollToPosition(ScrollPositionId.TOP, 275, 250);
    this._uiService.nextPageClicked$.next();
  }

  private scrollToPosition(
    positionId: ScrollPositionId,
    timeout: number,
    offset: number,
  ): void {
    this._page.scrollTo(positionId, { timeout, scrollOffset: offset });
  }

  private dispatchUserToStore(updatedFields: Partial<IUser> = {}): void {
    const { formGroup } = this;
    const isFormValid = formGroup.valid;
    const user = {
      ...this.formGroup.value,
      ...updatedFields,
      valid: this.formGroup.valid,
    } as IUser;

    this.selectedProduct$.pipe(take(1)).subscribe(({ id: productId }) => {
      this._store$.dispatch(
        this._createAction(new userDataActions.SelectUser(user)),
      );

      if (productId && isFormValid) {
        this._store$.dispatch(
          this._createAction(new receiptActions.AddUser({ user, productId })),
        );
      }
    });
  }

  private _refreshFormForNewUser(): void {
    this._uiService.getUserStorage();
    this.resetValue();
  }

  private _createAction(action: IAction): IAction {
    return createAction(action, UserDataComponent);
  }

  private handleAvoEvents(
    eventType: AvoEventTypes,
    destination?: string,
    formTagId?: string,
    errorMessage?: string,
    date?: Date
  ): void {
    switch (eventType) {
      case "button_click":
        const props = {
          uniqueName: "confirmation_of_user_information",
          description: "gegevens_kaarthouder",
          clickDestination: "https://www.ns.nl/businesscardaanvragen/#/bedrijf",
          clickText: "Ga verder met bestellen",
          data: undefined,
        };
        const trackingData = this._avoTrackingService.trackButtonClick(props);

        this._avoService.buttonClick(trackingData);
        break;
      case "link_click":
        this._avoTrackingService.trackBackLinkClick(
          "https://www.ns.nl/businesscardaanvragen",
        );
        break;
      case "form_start":
        this._avoService.formStart({
          formName: "user_data_name",
          formId1: formTagId,
        });
        break;
      case "form_error":
        this._avoService.formError({
          formName: "user_data_name",
          formId1: formTagId,
          errorMessage: [errorMessage],
        });
        break;
      case "selectable_click":
        this._avoService.selectableClick({
          uniqueName: destination,
          description: destination + "_selected",
          websiteSection: "ns-zakelijk|checkout|2-gegevens-kaarthouder",
          clickText: "nvt",
          optionList: ["onDate", "directly"],
          selectedOption: destination,
          optionAmount: null,
        });
        break;
      case "add_extra_card_link_click":
        this._avoTrackingService.trackAddExtraCardLinkClick(destination);

        break;
      case "datepickerClick":
        this._avoService.datepickerClick({
          selectedDate: moment(date).format("DD-MM-YYYY"),
          websiteSection: "ns-zakelijk|checkout|2-gegevens-kaarthouder",
          uniqueName: "user_selected_date",
          description: "user selected date for product usage",
        });

        break;
      case "form_submit":
        this._avoService.formSubmit({
          formId1: "user_data_name",
          formDestination: "pagina om bedrijfsgegevens in te vullen",
          permissions: [],
          clickText: "Ga verder met bestellen",
          formName: "user_data_name"
        })
        console.warn(`Unhandled event type: ${eventType}`);
        break;
      default:
        console.warn(`Unhandled event type: ${eventType}`);
    }
  }
}
